/**
 * client/src/structure/EvidenceRail.tsx — every signal the detector weighed, and what it decided.
 *
 * This is the component that makes the structure screen fast to use, and it is worth saying why,
 * because it looks like decoration. §3.4 does not pick where a book begins with a rule — it scores
 * eight signals, records the winning reason **verbatim**, keeps the losing candidates **with their
 * scores**, and stores the lot in `editions.bodyStartEvidence`. Rendering that is the difference
 * between a human reading the first three chapters of a book to work out whether the machine was
 * right, and a human reading one line — *"a numbered chapter series starts at CHAPTER I., and the
 * copyright fingerprint fired on the page before it"* — and disagreeing in one tap.
 *
 * A confidence with no evidence behind it is a number nobody can argue with. That is the whole
 * reason this file exists rather than a percentage in the corner of the chapter list.
 *
 * ⚠️ Every dereference into the payload is optional. This data crosses the network as `unknown` and
 * is asserted, so `tsc` is never asked whether a field arrived — and for the whole of P1 one of them
 * did not: the route sent no `runnerUps`, `view.runnerUps.length` threw inside render, and with no
 * ErrorBoundary in `client/src` the TypeError unmounted the React root. A **white screen** on
 * `/b/:id/structure`, with two clean 200s in the server log. An empty section is the honest failure;
 * a missing screen is not.
 */

import type { EvidenceRow, StructureView } from "../lib/api.ts";
import { IconAlert, IconCheck } from "../App.tsx";

export interface EvidenceRailProps {
  view: StructureView | null;
  loading: boolean;
  error: Error | null;
  /** The edition row's own copy, so the decision paints before the evidence lands. */
  fallback: { bodyStartSid: string | null; bodyStartConfidence: number | null; bodyStartReason: string | null; structureConfirmedAt: number | null };
  onConfirm: () => void;
  confirming: boolean;
  confirmError: string | null;
}

export function EvidenceRail({ view, loading, error, fallback, onConfirm, confirming, confirmError }: EvidenceRailProps) {
  // §3.4's "one column, two consumers": the edition row and the structure endpoint cannot disagree
  // about where the book begins, because there is only one column. So the decision paints from
  // whichever arrived first rather than holding the screen for the richer answer.
  const sid = view?.bodyStartSid ?? fallback.bodyStartSid;
  const confidence = view?.bodyStartConfidence ?? fallback.bodyStartConfidence;
  const reason = view?.bodyStartReason ?? fallback.bodyStartReason;
  const confirmedAt = view?.structureConfirmedAt ?? fallback.structureConfirmedAt;
  const startChapter = view?.chapters.find((c) => c.isBodyStart) ?? null;
  const evidence: EvidenceRow[] = view?.evidence ?? [];
  const runnerUps = view?.runnerUps ?? [];

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">The decision</h2>

      {sid === null ? (
        <p className="ab-refusal-title">The detector didn&rsquo;t find a start. The book reads from the top.</p>
      ) : (
        <>
          <div className="ab-decision">
            <div className="ab-decision-main">
              <span className="ab-eyebrow">Starts at</span>
              <strong className="ab-decision-title">{startChapter?.title ?? sid}</strong>
              {startChapter && <p className="ab-decision-preview">{startChapter.preview}</p>}
              {/* The sid, on screen. It is the only coordinate system in this product (§2.1), and
                  when something is in the wrong place this is the string worth reading out. */}
              <code className="ab-sid">{sid}</code>
            </div>

            <div className="ab-decision-side">
              <Confidence value={confidence} />
              <p className="ab-decision-state">
                {confirmedAt !== null ? (
                  <>
                    <IconCheck /> Confirmed
                  </>
                ) : (
                  "Not confirmed"
                )}
              </p>
            </div>
          </div>

          {reason && <p className="ab-decision-reason">{reason}</p>}
        </>
      )}

      {loading && !view && <p className="ab-hint">Loading the evidence…</p>}
      {error && !view && <p className="ab-hint ab-hint-bad">Couldn&rsquo;t load the evidence: {error.message}</p>}

      {confirmedAt === null && (
        <div className="ab-actions">
          <button type="button" className="ab-btn ab-btn-primary" onClick={onConfirm} disabled={confirming}>
            {confirming ? "Confirming…" : "This is right — confirm it"}
          </button>
          {/* LAW 6 stated where it matters. An unconfirmed book still plays — the gate exists to keep
              a *sweep* off a book whose structure nobody has looked at, not to stop one person
              pressing play on one page. Saying so here stops the button reading as a threat. */}
          <span className="ab-hint ab-hint-quiet">
            Confirming unlocks narrating a whole chapter, and warms the first three pages so the first
            press of play is instant. Playing one page has never needed it.
          </span>
        </div>
      )}
      {confirmError && <p className="ab-hint ab-hint-bad">{confirmError}</p>}

      {evidence.length > 0 && (
        <>
          <h3 className="ab-h3">Every signal, scored</h3>
          <table className="ab-table">
            <thead>
              <tr>
                <th>Signal</th>
                <th className="ab-num-col">Score</th>
                <th>What it saw</th>
              </tr>
            </thead>
            <tbody>
              {evidence.map((e, i) => (
                <tr key={`${e.name}-${i}`} className={e.score < 0 ? "is-negative" : e.score > 0 ? "is-positive" : ""}>
                  <td>
                    {e.signal !== undefined && <span className="ab-signal-no">{e.signal}</span>}
                    {e.name}
                  </td>
                  <td className="ab-num-col ab-num" data-label="Score">
                    {e.score > 0 ? `+${e.score}` : e.score}
                  </td>
                  <td className="ab-table-detail" data-label="Saw">
                    {e.detail ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {runnerUps.length > 0 && (
        <>
          <h3 className="ab-h3">The candidates that lost</h3>
          <ul className="ab-runners">
            {runnerUps.map((r) => (
              <li key={r.chapterOrd}>
                <span className="ab-runner-score">{r.score}</span>
                <span className="ab-runner-title">{r.title}</span>
                {r.reason && <span className="ab-runner-why">{r.reason}</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      {view?.needsReview && (
        <p className="ab-note ab-note-warn">
          <IconAlert />
          <span>This edition is flagged for review — narration is held until a human looks at it.</span>
        </p>
      )}
    </section>
  );
}

function Confidence({ value }: { value: number | null }) {
  if (value === null) {
    // LAW 9's discipline at the UI: a missing number is a missing number, never a confident zero.
    return (
      <div className="ab-confidence-block">
        <span className="ab-confidence-value">—</span>
        <span className="ab-confidence-label">no confidence recorded</span>
      </div>
    );
  }
  const pct = Math.round(value * 100);
  const band = value >= 0.9 ? "high" : value >= 0.6 ? "mid" : "low";
  return (
    <div className={`ab-confidence-block is-${band}`}>
      <span className="ab-confidence-value">{pct}%</span>
      <span className="ab-confidence-label">
        {band === "high" ? "decisive — auto-confirmed" : band === "mid" ? "a good guess" : "a guess worth checking"}
      </span>
      <div className="ab-meter" aria-hidden>
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
